/**
 * wallSlide.ts
 * Wall detection and the slowed descent Nova gets when she holds into a wall.
 *
 * Gated on `canWallSlide`, which unlocks at World 4 (ADR-009). The slide only
 * clamps descent speed — it does not stick Nova to the wall or alter her
 * horizontal motion, so it can never trap the player against geometry.
 *
 * @module wallSlide
 */

import { aabbOverlaps } from './collision';
import {
  NOVA_WIDTH,
  NOVA_HEIGHT,
  WALL_PROBE_DISTANCE,
  WALL_SLIDE_MAX_FALL_SPEED,
} from '../../constants/gameConstants';
import type { AABB } from '../types/entity';
import type { NovaRuntime } from './gameStateTypes';

/** Horizontal side a wall was found on. */
export type WallSide = -1 | 0 | 1;

/**
 * Probe for a solid wall immediately beside Nova.
 *
 * Collision resolution leaves a sub-pixel gap between Nova and the surface she
 * stopped against, so an exact-overlap test would report no wall the instant
 * after she hits one. The probe box is nudged {@link WALL_PROBE_DISTANCE} px
 * sideways to span that gap.
 *
 * @param nova - Nova's runtime state, read for position only.
 * @param platforms - Solid platforms to test against.
 * @param colliderTop - World Y of the collider's top edge (crouch-aware).
 * @param colliderHeight - Height of the active collider in pixels.
 * @returns -1 for a wall on the left, 1 on the right, 0 for neither. Walls on
 * both sides also return 0: in a corridor exactly Nova's width there is no
 * side to slide against, and picking one arbitrarily would let her cling in a
 * dead end she should be falling out of.
 */
export function detectWallSide(
  nova: NovaRuntime,
  platforms: readonly AABB[],
  colliderTop: number = nova.y,
  colliderHeight: number = NOVA_HEIGHT,
): WallSide {
  const left: AABB = {
    x: nova.x - WALL_PROBE_DISTANCE,
    y: colliderTop,
    width: NOVA_WIDTH,
    height: colliderHeight,
  };
  const right: AABB = { ...left, x: nova.x + WALL_PROBE_DISTANCE };

  let hitLeft = false;
  let hitRight = false;
  for (const platform of platforms) {
    if (!hitLeft && aabbOverlaps(left, platform)) hitLeft = true;
    if (!hitRight && aabbOverlaps(right, platform)) hitRight = true;
    if (hitLeft && hitRight) break;
  }

  if (hitLeft && !hitRight) return -1;
  if (hitRight && !hitLeft) return 1;
  return 0;
}

/**
 * Apply the wall-slide descent clamp for this tick.
 *
 * @param nova - Nova's runtime state; `vy` and `isWallSliding` are written.
 * @param platforms - Solid platforms to probe for a wall.
 * @param pressingLeft - Whether left input is held this tick.
 * @param pressingRight - Whether right input is held this tick.
 * @param colliderTop - World Y of the collider's top edge (crouch-aware).
 * @param colliderHeight - Height of the active collider in pixels.
 * @returns True when a slide is active this tick.
 */
export function applyWallSlide(
  nova: NovaRuntime,
  platforms: readonly AABB[],
  pressingLeft: boolean,
  pressingRight: boolean,
  colliderTop: number = nova.y,
  colliderHeight: number = NOVA_HEIGHT,
): boolean {
  nova.isWallSliding = false;
  // Only descent qualifies: clamping while rising would cut jumps short, and
  // an unlocked ability must never make a normal jump worse.
  if (nova.canWallSlide !== true || nova.isGrounded || nova.vy <= 0) return false;

  const side = detectWallSide(nova, platforms, colliderTop, colliderHeight);
  if (side === 0) return false;

  const pressingIntoWall = side === -1 ? pressingLeft : pressingRight;
  if (!pressingIntoWall) return false;

  nova.vy = Math.min(nova.vy, WALL_SLIDE_MAX_FALL_SPEED);
  nova.isWallSliding = true;
  return true;
}
